import {
  CompanyName,
  // CompanyPhoneNumber,
  FULL_BUSINESS_URL,
  INSTAGRAM_URL,
  TIKTOK_URL,
  UPWORK_URL,
} from '../Constants';

/* =========================
   Home page
========================= */
export const homePageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: CompanyName,
  url: FULL_BUSINESS_URL,
  description: `${CompanyName} offers commercials, UGC creation, and wellness coaching from Drammen, Norway — available worldwide.`,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${FULL_BUSINESS_URL}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
  sameAs: [INSTAGRAM_URL, TIKTOK_URL, UPWORK_URL].filter(Boolean),
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    areaServed: 'Worldwide',
    availableLanguage: ['English', 'Norwegian'],
  },
};

export const homePageAdditionalMeta = [
  { property: 'og:title', content: `${CompanyName} — Commercials, UGC & Wellness Coaching` },
  {
    property: 'og:description',
    content: `${CompanyName} creates commercials and UGC, and offers wellness coaching services worldwide.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
  { property: 'og:url', content: FULL_BUSINESS_URL },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `${CompanyName} — Commercials, UGC & Wellness Coaching` },
  {
    name: 'twitter:description',
    content: `${CompanyName} provides professional commercials, UGC, and wellness coaching globally.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
];

// About page
export const aboutPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `About ${CompanyName}`,
  url: `${FULL_BUSINESS_URL}/about`,
  description: `Learn more about Siv Renate, her experience in commercials and UGC creation, and her commitment to wellness coaching for clients worldwide.`,
  sameAs: [INSTAGRAM_URL, TIKTOK_URL, UPWORK_URL],
  publisher: {
    '@type': 'Person',
    name: 'Siv Renate Skaufel',
  },
};

export const aboutPageAdditionalMeta = [
  { property: 'og:title', content: `About ${CompanyName}` },
  {
    property: 'og:description',
    content: `Discover Siv Renate’s story, her creative work in commercials, and her passion for supporting clients through wellness coaching.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/about` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `About ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Explore Siv Renate’s journey — from creative UGC and commercials to guiding clients through personal growth and wellness coaching.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
];

/* =========================
   Commercials (Portfolio) page
========================= */
export const commercialsPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: `${CompanyName} — Commercials & UGC Portfolio`,
  url: `${FULL_BUSINESS_URL}/commercials`,
  description:
    `Explore commercials and UGC projects by ${CompanyName}. Human-first content that connects with audiences worldwide.`,
  sameAs: [INSTAGRAM_URL, TIKTOK_URL, UPWORK_URL].filter(Boolean),
  isPartOf: {
    '@type': 'WebSite',
    name: CompanyName,
    url: FULL_BUSINESS_URL,
  },
};

export const commercialsPageAdditionalMeta = [
  { property: 'og:title', content: `Commercials & UGC Portfolio | ${CompanyName}` },
  {
    property: 'og:description',
    content: `See featured commercials and UGC work by ${CompanyName}.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/commercials` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Commercials & UGC Portfolio | ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Browse commercials and UGC projects crafted by ${CompanyName}.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
];

/* =========================
   Wellness page
========================= */
export const wellnessPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: `${CompanyName} — Wellness Coaching`,
  serviceType: 'Wellness Coaching',
  provider: {
    '@type': 'Person',
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    sameAs: [INSTAGRAM_URL, TIKTOK_URL, UPWORK_URL].filter(Boolean),
  },
  areaServed: 'Worldwide',
  availableChannel: {
    '@type': 'ServiceChannel',
    serviceUrl: `${FULL_BUSINESS_URL}/wellness`,
    availableLanguage: ['English', 'Norwegian'],
  },
  description:
    `Personalized wellness coaching with ${CompanyName}: clarity sessions, 4-week resets, and 8-week transformations — online, worldwide.`,
};

export const wellnessPageAdditionalMeta = [
  { property: 'og:title', content: `Wellness Coaching | ${CompanyName}` },
  {
    property: 'og:description',
    content: `Gentle, practical coaching for stress management, resilience, and balance — available worldwide.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/wellness` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Wellness Coaching | ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Work with ${CompanyName} for supportive, results-focused wellness coaching.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
];

/* =========================
   Newsletter pages
========================= */
export const newsletterPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `${CompanyName} Newsletter Sign-Up`,
  url: `${FULL_BUSINESS_URL}/newsletter`,
  description: `Subscribe for updates on commercials/UGC projects and wellness coaching from ${CompanyName}.`,
  sameAs: [INSTAGRAM_URL, TIKTOK_URL, UPWORK_URL].filter(Boolean),
  publisher: {
    '@type': 'Person',
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${FULL_BUSINESS_URL}/brand/logo.png`,
    },
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    areaServed: 'Worldwide',
    availableLanguage: ['English', 'Norwegian'],
  },
};

export const newsletterPageAdditionalMeta = [
  { property: 'og:title', content: `Subscribe to ${CompanyName} Newsletter` },
  {
    property: 'og:description',
    content: `Get updates on new commercials, UGC, and wellness coaching sessions from ${CompanyName}.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/newsletter` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Subscribe to ${CompanyName} Newsletter` },
  {
    name: 'twitter:description',
    content: `Join the list for news and availability from ${CompanyName}.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
];

export const newsletterValidationStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Newsletter Email Confirmation',
  url: `${FULL_BUSINESS_URL}/newsletter/confirm-email`,
  description: `Confirmation page for newsletter subscription to ${CompanyName}.`,
  mainEntity: {
    '@type': 'Person',
    name: CompanyName,
    url: FULL_BUSINESS_URL,
  },
};

export const newsletterValidationAdditionalMeta = [
  { property: 'og:title', content: `Confirm your subscription – ${CompanyName}` },
  {
    property: 'og:description',
    content: `Thanks for subscribing to updates from ${CompanyName}. This confirms your email address.`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/newsletter/confirm-email` },
  { name: 'robots', content: 'noindex, follow' },
];

export const newsletterUnsubscribeStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `${CompanyName} Newsletter Unsubscribe`,
  url: `${FULL_BUSINESS_URL}/newsletter/unsubscribe`,
  description: `Unsubscribe from ${CompanyName} updates.`,
  mainEntity: {
    '@type': 'Person',
    name: CompanyName,
    url: FULL_BUSINESS_URL,
  },
};

export const newsletterUnsubscribeAdditionalMeta = [
  { property: 'og:title', content: `Unsubscribe from ${CompanyName} Newsletter` },
  {
    property: 'og:description',
    content: `You've chosen to unsubscribe from ${CompanyName} updates.`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/newsletter/unsubscribe` },
  { name: 'robots', content: 'noindex, follow' },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Unsubscribe – ${CompanyName} Newsletter` },
  {
    name: 'twitter:description',
    content: `Manage your email preferences for ${CompanyName}.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
];

/* =========================
   Reviews page
========================= */
export const reviewsPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `${CompanyName} Reviews`,
  url: `${FULL_BUSINESS_URL}/reviews`,
  description: `Read client reviews and testimonials about commercials, UGC, and wellness coaching by ${CompanyName}.`,
  sameAs: [INSTAGRAM_URL, TIKTOK_URL, UPWORK_URL].filter(Boolean),
  potentialAction: {
    '@type': 'SearchAction',
    target: `${FULL_BUSINESS_URL}/reviews?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const reviewsPageAdditionalMeta = [
  { property: 'og:title', content: `Client Reviews | ${CompanyName}` },
  {
    property: 'og:description',
    content: `See what clients say about working with ${CompanyName} on commercials, UGC, and wellness coaching.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/reviews` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Client Reviews | ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Testimonials for ${CompanyName}'s commercials, UGC, and wellness coaching.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
];

/* =========================
   Booking page
========================= */
export const bookingPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: `${CompanyName} Booking`,
  serviceType: 'Commercials & Wellness Coaching — Booking',
  provider: {
    '@type': 'Person',
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    sameAs: [INSTAGRAM_URL, TIKTOK_URL, UPWORK_URL].filter(Boolean),
  },
  areaServed: 'Worldwide',
  availableChannel: {
    '@type': 'ServiceChannel',
    serviceUrl: `${FULL_BUSINESS_URL}/booking`,
    availableLanguage: ['English', 'Norwegian'],
  },
  description: `Schedule a discovery call or request pricing with ${CompanyName} for commercials/UGC or wellness coaching.`,
};

export const bookingPageAdditionalMeta = [
  { property: 'og:title', content: `Book with ${CompanyName}` },
  {
    property: 'og:description',
    content: `Schedule a discovery call or request a custom quote for commercials/UGC or wellness coaching.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/booking` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Book with ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Quickly book time with ${CompanyName} — worldwide availability.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
];

/* =========================
   Forgotten password
========================= */
export const forgottenPasswordPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Reset Password',
  description: `Reset your ${CompanyName} account password.`,
  url: `${FULL_BUSINESS_URL}/forgotten-password`,
};

export const forgottenPasswordPageAdditionalMeta = [
  { property: 'og:title', content: `Reset Your Password - ${CompanyName}` },
  { property: 'og:description', content: `Securely reset your ${CompanyName} account password.` },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/assets/images/pages/reset-password-preview.jpg` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/forgotten-password` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Reset Your Password - ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Follow the steps to securely reset your ${CompanyName} account password.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/assets/images/pages/reset-password-preview.jpg` },
];

/* =========================
   Maintenance
========================= */
export const maintenancePageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Maintenance Page',
  description: `The ${CompanyName} website is currently undergoing maintenance. We’ll be back soon!`,
  url: `${FULL_BUSINESS_URL}/maintenance`,
};

export const maintenancePageAdditionalMeta = [
  { property: 'og:title', content: `We'll Be Back Soon - ${CompanyName}` },
  {
    property: 'og:description',
    content: `The ${CompanyName} website is currently undergoing maintenance. We’ll be back shortly with updates.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/assets/images/pages/maintenance-preview.jpg` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/maintenance` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `We'll Be Back Soon - ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `The ${CompanyName} website is currently undergoing maintenance.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/assets/images/pages/maintenance-preview.jpg` },
];

/* =========================
   Policies page
========================= */
export const termsAndPoliciesStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms and Policies',
  description: `Explore the terms of service and privacy policies of ${CompanyName}.`,
  url: `${FULL_BUSINESS_URL}/terms-and-policies`,
  mainEntity: {
    '@type': 'FAQPage',
    name: 'Terms and Policies FAQ',
    description: `Frequently asked questions about terms of service and privacy at ${CompanyName}.`,
  },
};

export const termsAndPoliciesAdditionalMeta = [
  { property: 'og:title', content: `Terms and Policies - ${CompanyName}` },
  {
    property: 'og:description',
    content: `Understand the terms of service and privacy policies of ${CompanyName}.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/assets/images/pages/terms-preview.jpg` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/terms-and-policies` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Terms and Policies - ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Learn about our terms of service and privacy policies at ${CompanyName}.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/assets/images/pages/terms-preview.jpg` },
];

/* =========================
   Error page
========================= */
export const errorPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '404 Error Page',
  description: `The requested page could not be found on ${CompanyName}.`,
  url: `${FULL_BUSINESS_URL}/404`,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${FULL_BUSINESS_URL}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const errorPageAdditionalMeta = [
  { property: 'og:title', content: 'Page Not Found - 404' },
  {
    property: 'og:description',
    content: `Oops! The page you’re looking for isn’t here. Visit ${CompanyName} to find what you need.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/assets/images/pages/404-preview.jpg` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/404` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: 'Page Not Found - 404' },
  {
    name: 'twitter:description',
    content: `Oops! The page you’re looking for isn’t here. Visit ${CompanyName} to find what you need.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/assets/images/pages/404-preview.jpg` },
];

/* =========================
   Contact page
========================= */
export const contactPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  mainEntity: {
    '@type': 'Person',
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      areaServed: 'Worldwide',
      availableLanguage: ['English', 'Norwegian'],
    },
    sameAs: [INSTAGRAM_URL, TIKTOK_URL, UPWORK_URL].filter(Boolean),
    logo: {
      '@type': 'ImageObject',
      url: `${FULL_BUSINESS_URL}/brand/logo.png`,
    },
  },
};

export const contactPageAdditionalMeta = [
  { property: 'og:title', content: `Contact ${CompanyName}` },
  {
    property: 'og:description',
    content: `Reach out to ${CompanyName} for commercials/UGC or wellness coaching. Send an email to request pricing or book a discovery call.`,
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/brand/contact-preview.jpg` },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/contact` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Contact ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Contact ${CompanyName} to discuss commercials/UGC or wellness coaching — worldwide availability.`,
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/contact-preview.jpg` },
];
